import { Inject, Injectable, PLATFORM_ID, makeStateKey } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, of, tap, concatMap, forkJoin, map, catchError } from 'rxjs';
import { APPHOST } from '../../app.component';
import { isPlatformServer } from '@angular/common';
import { PrerenderDataService } from '../../prerender-data.service';

// interfaces/item.ts

export interface Item {
  id?: string;
  label?: string;
  embed?: string;
  icon?: string;
  slug?: string;
  newArrival?: boolean;
  discounted?: boolean;
  brandNew?: boolean;
  used?: boolean;
  previousPrice?: number;
  newPrice?: number;
  pleaseContactForPrice?: boolean;
  availability?: string;
  shortDescription?: string;
  fullSpec?: string;
  warranty?: string;
  image1?: string;
  image2?: string;
  image3?: string;
  image4?: string;
  image5?: string;
  image6?: string;
  category?: Category;
  tags?: string[];
  compatibleItems?: Item[]
}

export interface Category {
  id?: string;
  label?: string;
  icon?: string;
  slug?: string;
  showNewState?: boolean;
  mainNavItem?: MainNavItem;
  filters?: Filter[]
  menuOrder?: number;
}

export interface MainNavItem {
  id?: string;
  label?: string;
}


export interface Filter {
  id?: string; // Optional string id
  label?: string; // Optional string label
  filterItems?: FilterItem[]; // Optional array of ProductFilterItem objects
  category?: Category; // Optional ProductCategory object
}

export interface FilterItem {
  id?: string; // Optional string id
  label?: string; // Optional string label
}


@Injectable({
  providedIn: 'root'
})

export class ItemService {

  private apiItem = '';
  private apiCategory = '';
  private quotation = '';
  private contactUs = '';

  private categoryItemsCache = new Map<string, Item[]>();
  private categoriesCache: Category[] | null = null;
  private productCache = new Map<string, Item>();

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
    private prerenderDataService: PrerenderDataService
  ) {
    if (isPlatformServer(this.platformId)) {
      this.apiItem = 'https://api.softnetcomputers.lk/api/items/web';
      this.apiCategory = 'https://api.softnetcomputers.lk/api/categories/web';
      this.quotation = 'https://api.softnetcomputers.lk/api/quotation-requests';
      this.contactUs = 'https://api.softnetcomputers.lk/api/contact-us-inquiries';
    } else {
      this.apiItem = '/api/items/web';
      this.apiCategory = '/api/categories/web';
      this.quotation = '/api/quotation-requests';
      this.contactUs = '/api/contact-us-inquiries';
    }
  }

  getItem(id: string): Observable<Item> {
    if (this.productCache.has(id)) {
      return of(this.productCache.get(id)!);
    }
    const key = makeStateKey<any>('PROD_' + id);
    const preloaded = this.prerenderDataService.getPreloadedData(key);
    if (preloaded && preloaded.id) {
      this.productCache.set(id, preloaded);
      return of(preloaded);
    }

    const url = `${this.apiItem}/${id}`;
    return this.http.get<Item>(url).pipe(
      tap(item => {
        if (item) {
          this.productCache.set(id, item);
          this.prerenderDataService.setPreloadedData(key, item);
        }
      })
    );
  }

  getQuotationBody(id: string): Observable<any> {
    const url = `${this.quotation}/${id}`;
    return this.http.get<any>(url);
  }


  patchQuotation(body:any): Observable<any> {
    const url = `${this.quotation}/${body.id}`;
    return this.http.patch<any>(url,body);
  }

  getItemAll(slug: string): Observable<Item[]> {
    if (this.categoryItemsCache.has(slug)) {
      return of(this.categoryItemsCache.get(slug)!);
    }
    const key = makeStateKey<any>('CAT_ITEMS_' + slug);
    const preloaded = this.prerenderDataService.getPreloadedData(key);
    if (preloaded && Array.isArray(preloaded) && preloaded.length > 0) {
      this.categoryItemsCache.set(slug, preloaded);
      return of(preloaded);
    }

    // Check if store-wide items are available from search preloading
    const allSearchKey = makeStateKey<any>('GET_ALL_ITEMS_SEARCH');
    const allSearchItems: Item[] = this.prerenderDataService.getPreloadedData(allSearchKey);
    if (allSearchItems && Array.isArray(allSearchItems) && allSearchItems.length > 0) {
      const itemsForCat = allSearchItems.filter(item => item?.category?.slug === slug);
      if (itemsForCat.length > 0) {
        this.categoryItemsCache.set(slug, itemsForCat);
        this.prerenderDataService.setPreloadedData(key, itemsForCat);
        return of(itemsForCat);
      }
    }

    const url = `${this.apiItem}/items/${slug}`;
    return this.http.get<Item[]>(url).pipe(
      tap(items => {
        if (items) {
          this.categoryItemsCache.set(slug, items);
          this.prerenderDataService.setPreloadedData(key, items);
        }
      })
    );
  }


  getAllCats(): Observable<Category[]> {
    if (this.categoriesCache && this.categoriesCache.length > 0) {
      return of(this.categoriesCache);
    }
    const key = makeStateKey<any>('GET_ALL_CATS');
    const key2 = makeStateKey<any>('CAT_LIST');
    const preloaded = this.prerenderDataService.getPreloadedData(key) || this.prerenderDataService.getPreloadedData(key2);
    if (preloaded && Array.isArray(preloaded) && preloaded.length > 0) {
      this.categoriesCache = preloaded;
      return of(preloaded);
    }

    const url = `${this.apiCategory}?page=0&size=60`;
    return this.http.get<Category[]>(url).pipe(
      tap(cats => {
        if (cats) {
          this.categoriesCache = cats;
          this.prerenderDataService.setPreloadedData(key, cats);
          this.prerenderDataService.setPreloadedData(key2, cats);
        }
      })
    );
  }

  getHomeDealsAndNewArrivals(): Observable<{ deals: Item[]; newArrivals: Item[] }> {
    return this.getAllCats().pipe(
      concatMap(categories => {
        if (!categories || categories.length === 0) {
          return of({ deals: [], newArrivals: [] });
        }
        const observables = categories.map(cat =>
          this.getItemAll(cat.slug!).pipe(catchError(() => of([])))
        );
        return forkJoin(observables).pipe(
          map(itemsArrays => {
            const allItems: Item[] = [];
            itemsArrays.forEach(items => {
              if (Array.isArray(items)) {
                allItems.push(...items);
              }
            });
            const validItems = allItems.filter(o => o && o.slug && o.slug !== '');

            const deals = this.pickRandomItems(
              validItems.filter(p => p.discounted && p.availability !== 'OUT_OF_STOCK'),
              15
            );
            const newArrivals = this.pickRandomItems(
              validItems.filter(p => p.newArrival && p.availability !== 'OUT_OF_STOCK'),
              15
            );
            return { deals, newArrivals };
          })
        );
      })
    );
  }

  private pickRandomItems(arr: Item[], count: number): Item[] {
    if (!arr || arr.length === 0) return [];
    if (arr.length <= count) return [...arr];
    const picked: Item[] = [];
    const indexes: number[] = [];
    for (let i = 0; i < count; i++) {
      let randomIndex = Math.floor(Math.random() * arr.length);
      while (indexes.includes(randomIndex)) {
        randomIndex = Math.floor(Math.random() * arr.length);
      }
      indexes.push(randomIndex);
      picked.push(arr[randomIndex]);
    }
    return picked;
  }

  getQuotation(obj: any): Observable<HttpResponse<Blob>> {
    const url = `/application-form`;

    return this.http.post(url, obj, {
      responseType: 'blob' as 'blob', // Explicitly specify responseType
      observe: 'response' // Return the full HttpResponse object
    })
  }


  postQuotation(body: any, token: string = ''): Observable<HttpResponse<any>> {

    return this.http.post(this.quotation + '?token=' + token, body,{observe:'response'})
  }



  cicdhc(): Observable<HttpResponse<any>> {
    return this.http.get<any>(APPHOST+'/deploy-softnet')
  }


  postContactUs(body: any, token: string = '') {

    return this.http.post(this.contactUs + '?token=' + token, body)
  }

}
